namespace $ {

	export type $shm_hitalama_transfer_field_kind =
		| 'atom'
		| 'text'
		| 'list'
		| 'link'
		| 'links'
		| 'owned'

	export type $shm_hitalama_transfer_field =
		| { kind: 'atom', value: any }
		| { kind: 'text', value: string }
		| { kind: 'list', value: readonly any[] }
		| { kind: 'link', link: string | null, class_name?: string }
		| { kind: 'links', links: readonly string[], class_name?: string }
		| { kind: 'owned', link: string | null, class_name?: string }

	export type $shm_hitalama_transfer_node = {
		link: string
		title?: string
		class_name?: string
		fields: Record< string, $shm_hitalama_transfer_field >
		table2_values?: readonly [ field_link: string, value: any ][]
		entries?: readonly [ key: string, field: $shm_hitalama_transfer_field ][]
		items?: readonly $shm_hitalama_transfer_field[]
		blob_uri?: string
	}

	export type $shm_hitalama_transfer_dump = {
		format: 'hitalama_transfer'
		root_link: string
		nodes: $shm_hitalama_transfer_node[]
	}

	export type $shm_hitalama_transfer_node_context = {
		owner: $giper_baza_dict
		owner_link: string
		field: string
		kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >
		node: $giper_baza_dict
		node_link: string
	}

	export type $shm_hitalama_transfer_dto_context = {
		owner: any
		owner_link: string
		field: string
		kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >
		node: $shm_hitalama_transfer_node
	}

	export class $shm_hitalama_transfer extends $mol_object {

		entity(): $giper_baza_dict {
			throw new Error('Not implemented')
		}

		protected should_serialize_field(
			owner: $giper_baza_dict,
			field: string,
			_field_pawn: any,
		) {
			if( owner instanceof $giper_baza_file && field === 'Chunks' ) return false
			return true
		}

		protected should_serialize_node( _context: $shm_hitalama_transfer_node_context ) {
			return true
		}

		protected should_deserialize_node( _context: $shm_hitalama_transfer_dto_context ) {
			return true
		}

		async blob_uri_async( link: string ) {
			const file = this.$.$giper_baza_glob.Pawn( new $giper_baza_link( link ), $giper_baza_file )
			const blob = await $mol_wire_async(file).blob()
			return await $mol_blob_uri( blob )
		}
		
		@ $mol_mem_key
		protected blob_uri( link: string ) {
			return $mol_wire_sync( this ).blob_uri_async( link )
		}

		normalize_legacy_names() {
			return true
		}

		protected normalize_field_value( field: string, value: any ) {
			if( typeof value !== 'string' ) return value
			if( this.normalize_legacy_names() && [ 'Text', 'Subtext', 'Head_method', 'Rows_method', 'Data_method' ].includes( field ) ) {
				return value.replaceAll( 'board', 'map' )
			}
			return value
		}

		protected class_name_of_pawn( Pawn: any ) {
			if( !Pawn ) return undefined
			return this.$.$mol_func_name( Pawn )
		}

		protected class_name_of_link_value( pawn: any ) {
			return this.class_name_of_pawn( pawn?.Value?.() )
		}

		protected serialize_field(
			field_pawn: any,
		): $shm_hitalama_transfer_field | undefined {
			if( !field_pawn ) return undefined

			if( field_pawn instanceof $giper_baza_atom_link ) {
				return {
					kind: 'link',
					link: field_pawn.val()?.resolve( field_pawn.link() ).str ?? null,
					class_name: this.class_name_of_link_value( field_pawn ),
				}
			}

			if( field_pawn instanceof $giper_baza_list_link ) {
				return {
					kind: 'links',
					links: ( field_pawn as any ).remote_list().map( ( item: $giper_baza_pawn )=> item.link().toString() ),
					class_name: this.class_name_of_pawn( ( field_pawn.constructor as any )?.Value?.() ),
				}
			}

			if( field_pawn instanceof $giper_baza_text ) {
				return {
					kind: 'text',
					value: field_pawn.value(),
				}
			}

			if( field_pawn instanceof $giper_baza_atom_vary ) {
				return {
					kind: 'atom',
					value: ( field_pawn as any ).val(),
				}
			}

			if( field_pawn instanceof $giper_baza_dict ) {
				return {
					kind: 'owned',
					link: field_pawn.link().toString(),
					class_name: this.class_name_of_pawn( field_pawn.constructor ),
				}
			}

			if( field_pawn instanceof $giper_baza_list ) {
				const value = typeof ( field_pawn as any ).items === 'function'
					? ( field_pawn as any ).items()
					: field_pawn.items_vary()

				return {
					kind: 'list',
					value,
				}
			}
		}

		protected follow_serialize_node(
			owner: $giper_baza_dict,
			field: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			target: $giper_baza_dict | null,
			visited: Map< string, $shm_hitalama_transfer_node >,
		) {
			if( !target ) return

			const context: $shm_hitalama_transfer_node_context = {
				owner,
				owner_link: owner.link().toString(),
				field,
				kind,
				node: target,
				node_link: target.link().toString(),
			}

			if( !this.should_serialize_node( context ) ) return
			this.serialize_node( target, visited )
		}

		protected serialize_node(
			entity: $giper_baza_dict,
			visited = new Map< string, $shm_hitalama_transfer_node >(),
		) {
			const link = entity.link().toString()
			const cached = visited.get( link )
			if( cached ) return cached

			const Pawn = entity.constructor as typeof $giper_baza_dict & {
				schema?: Record< string, typeof $giper_baza_pawn >
			}

			const node: $shm_hitalama_transfer_node = {
				link,
				title: typeof ( entity as any ).title === 'function' ? ( entity as any ).title() : undefined,
				class_name: this.class_name_of_pawn( Pawn ),
				fields: {},
			}
			visited.set( link, node )

			if( entity instanceof $giper_baza_text ) {
				node.fields.Value = {
					kind: 'text',
					value: entity.value(),
				}
			}

			const schema = Pawn.schema ?? {}
			for( const field in schema ) {
				const field_pawn = ( entity as any )[ field ]?.call( entity )
				if( !this.should_serialize_field( entity, field, field_pawn ) ) continue
				const serialized = this.serialize_field( field_pawn )
				if( !serialized ) continue

				node.fields[ field ] = serialized

				if( serialized.kind === 'owned' && serialized.link ) {
					this.follow_serialize_node( entity, field, 'owned', field_pawn, visited )
				}

				if( serialized.kind === 'link' && serialized.link ) {
					this.follow_serialize_node(
						entity,
						field,
						'link',
						( field_pawn as any )?.remote?.() ?? null,
						visited,
					)
				}

				if( serialized.kind === 'links' && serialized.links.length ) {
					for( const remote of ( field_pawn as any )?.remote_list?.() ?? [] ) {
						this.follow_serialize_node( entity, field, 'links', remote ?? null, visited )
					}
				}
			}

			if( entity instanceof $giper_baza_list_link ) {
				const items: $shm_hitalama_transfer_field[] = []
				for( const remote of ( entity as any ).remote_list?.() ?? [] ) {
					const serialized: $shm_hitalama_transfer_field = {
						kind: 'link',
						link: remote?.link().toString() ?? null,
						class_name: this.class_name_of_pawn( remote?.constructor ),
					}
					items.push( serialized )
					if( serialized.link ) {
						this.follow_serialize_node( entity, 'items', 'links', remote ?? null, visited )
					}
				}
				if( items.length ) node.items = items
			}

			if(
				!Object.keys( schema ).length
				&& typeof ( entity as any ).keys === 'function'
				&& typeof ( entity as any ).key === 'function'
			) {
				const entries: [ key: string, field: $shm_hitalama_transfer_field ][] = []
				for( const key of ( entity as any ).keys() as readonly any[] ) {
					const field_pawn = ( entity as any ).key( key )
					if( !this.should_serialize_field( entity, String( key ), field_pawn ) ) continue
					const serialized = this.serialize_field( field_pawn )
					if( !serialized ) continue

					entries.push([ String( key ), serialized ])
					if( serialized.kind === 'owned' && serialized.link ) {
						this.follow_serialize_node( entity, String( key ), 'owned', field_pawn, visited )
					}

					if( serialized.kind === 'link' && serialized.link ) {
						this.follow_serialize_node(
							entity,
							String( key ),
							'link',
							( field_pawn as any )?.remote?.() ?? null,
							visited,
						)
					}

					if( serialized.kind === 'links' && serialized.links.length ) {
						for( const remote of ( field_pawn as any )?.remote_list?.() ?? [] ) {
							this.follow_serialize_node( entity, String( key ), 'links', remote ?? null, visited )
						}
					}
				}
				if( entries.length ) node.entries = entries
			}

			if( entity instanceof $giper_baza_file ) {
				node.blob_uri = this.blob_uri( link )
			}

			return node
		}

		serialize(): $shm_hitalama_transfer_dump {
			const visited = new Map< string, $shm_hitalama_transfer_node >()
			const root_link = this.entity().link().toString()
			this.serialize_node( this.entity(), visited )
			const nodes = [ ...visited.values() ]

			return {
				format: 'hitalama_transfer',
				root_link,
				nodes,
			}
		}

		pawn_by_old_link = new Map< string, any >
		node_by_old_link = new Map< string, $shm_hitalama_transfer_node >
		protected blobs_by_uri = new Map< string, Blob >
		private instantiating = new Set< string >

		protected instantiate_mapped_child( link: string ) {
			const pawn = this.pawn_by_old_link.get( link )
			if( !pawn ) return false
			// Mapping is published before descendants are built. A suspended child
			// must resume its subtree rather than treating that mapping as completion.
			const node = this.node_by_old_link.get( link )
			if( node ) this.instantiate_children( pawn, node )
			return true
		}

		protected set_mapping( old_link: string, pawn: any ) {
			this.pawn_by_old_link.set( old_link, pawn )
		}

		protected set_title( entity: any, dto: $shm_hitalama_transfer_node ) {
			if( dto.title === undefined ) return
			if( typeof entity.title !== 'function' ) return
			entity.title( dto.title === dto.link ? entity.link().toString() : dto.title )
		}

		protected should_instantiate_child(
			host: any,
			field: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			link: string,
		) {
			const node = this.node_by_old_link.get( link )
			if( !node ) return false

			return this.should_deserialize_node({
				owner: host,
				owner_link: host.link().toString(),
				field,
				kind,
				node,
			})
		}

		protected child_config( host: any ) {
			return host?.land?.() ?? null
		}

		@ $mol_action
		protected instantiate_field_node( host: any, field: string, link: string, config?: any ) {
			const accessor = host[ field ]?.call( host, null )
			if( config === undefined ) config = this.child_config( host )
			return accessor?.make?.( config )
		}

		@ $mol_action
		protected instantiate_entry_node( host: any, key: string, link: string ) {
			const item = host.key( key, 'auto' )
			return item?.make?.( this.child_config( host ) )
		}

		protected instantiate_field_child(
			host: any,
			field: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			link: string | null,
		) {
			if( !link ) return
			if( this.instantiate_mapped_child( link ) ) return
			if( !this.should_instantiate_child( host, field, kind, link ) ) return

			const child_dto = this.node_by_old_link.get( link )
			if( !child_dto ) return

			const accessor = kind === 'links' ? null : host[ field ]?.call( host, null )
			const child = kind === 'owned' ? accessor : kind === 'link'
				? accessor?.ensure?.( this.child_config( host ) )
				: this.instantiate_field_node( host, field, link )

			if( !child ) return

			this.set_mapping( link, child )
			this.instantiate_children( child, child_dto )
		}

		protected instantiate_entry_child(
			host: any,
			key: string,
			kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
			link: string | null,
		) {
			if( !link ) return
			if( this.instantiate_mapped_child( link ) ) return
			if( typeof host.key !== 'function' ) return
			if( !this.should_instantiate_child( host, key, kind, link ) ) return

			const child_dto = this.node_by_old_link.get( link )
			if( !child_dto ) return

			const item = kind === 'links' ? null : host.key( key, 'auto' )
			const child = kind === 'owned' ? item : kind === 'link'
				? item?.ensure?.( this.child_config( host ) )
				: this.instantiate_entry_node( host, key, link )

			if( !child ) return

			this.set_mapping( link, child )
			this.instantiate_children( child, child_dto )
		}

		protected instantiate_children(
			host: any,
			dto: $shm_hitalama_transfer_node,
		) {
			// References may lead back to an ancestor (including self-references).
			if( this.instantiating.has( dto.link ) ) return
			this.instantiating.add( dto.link )
			try { return this.instantiate_children_apply( host, dto ) }
			finally { this.instantiating.delete( dto.link ) }
		}

		@ $mol_action
		protected instantiate_list_child( host: any, node: $shm_hitalama_transfer_node ) {
			// Give each logical child its own wire task. A suspension in a later
			// item must not reuse make(config) from a previously mapped sibling.
			return host.make( this.child_config( host ) )
		}

		private instantiate_children_apply( host: any, dto: $shm_hitalama_transfer_node ) {
			if( dto.items?.length && typeof host.make === 'function' ) {
				dto.items.forEach( item_dto => {
					if( item_dto.kind !== 'link' || !item_dto.link ) return
					if( this.instantiate_mapped_child( item_dto.link ) ) return
					if( !this.should_instantiate_child( host, 'items', 'links', item_dto.link ) ) return

					const child_dto = this.node_by_old_link.get( item_dto.link )
					if( !child_dto ) return

					const child = this.instantiate_list_child( host, child_dto )
					if( !child ) return

					this.set_mapping( item_dto.link, child )
					this.instantiate_children( child, child_dto )
				} )
			}

			for( const field in dto.fields ) {
				const field_dto = dto.fields[ field ]
				if( field_dto.kind === 'owned' ) {
					this.instantiate_field_child( host, field, 'owned', field_dto.link )
					continue
				}
				if( field_dto.kind === 'link' ) {
					this.instantiate_field_child( host, field, 'link', field_dto.link )
					continue
				}
				if( field_dto.kind === 'links' ) {
					field_dto.links.forEach( link => this.instantiate_field_child( host, field, 'links', link ) )
				}
			}

			dto.entries?.forEach( ([ key, field_dto ]) => {
				if( field_dto.kind === 'owned' ) {
					this.instantiate_entry_child( host, key, 'owned', field_dto.link )
					return
				}
				if( field_dto.kind === 'link' ) {
					this.instantiate_entry_child( host, key, 'link', field_dto.link )
					return
				}
				if( field_dto.kind === 'links' ) {
					field_dto.links.forEach( link => this.instantiate_entry_child( host, key, 'links', link ) )
				}
			} )
		}

		protected deserialize_field_data( host: any, field: string, dto: $shm_hitalama_transfer_field ) {
			if( dto.kind === 'link' || dto.kind === 'links' || dto.kind === 'owned' ) return

			const accessor = host[ field ]?.call( host, null )
			if( !accessor ) return

			switch( dto.kind ) {
				case 'atom':
					accessor.val?.( this.normalize_field_value( field, dto.value ) )
					return
				case 'text':
					accessor.value?.( this.normalize_field_value( field, dto.value ) )
					return
				case 'list':
					const value = this.normalize_field_value( field, dto.value )
					if( typeof accessor.items === 'function' ) accessor.items( value )
					else if( typeof accessor.items_vary === 'function' ) accessor.items_vary( value )
					else accessor.val?.( value )
					return
			}
		}

		protected deserialize_field_links( host: any, field: string, dto: $shm_hitalama_transfer_field ) {
			if( dto.kind === 'owned' ) return

			const accessor = host[ field ]?.call( host, null )
			if( !accessor ) return

			switch( dto.kind ) {
				case 'link': {
					if( !dto.link ) return
					const pawn = this.pawn_by_old_link.get( dto.link )
					if( typeof accessor.remote === 'function' ) {
						if( pawn ) accessor.remote( pawn )
					} else if( accessor instanceof $giper_baza_atom_link ) {
						accessor.val( pawn?.link() ?? new $giper_baza_link( dto.link ) )
					}
					return
				}
				case 'links': {
					const links = dto.links.flatMap( link => {
						const pawn = this.pawn_by_old_link.get( link )
						if( pawn ) return [ pawn ]
						return []
					} )
					accessor.remote_list?.()
					accessor.remote_list?.( links )
					return
				}
			}
		}

		protected deserialize_entries_data( host: any, dto: $shm_hitalama_transfer_node ) {
			if( !dto.entries?.length ) return
			if( typeof host.key !== 'function' ) return

			dto.entries.forEach( ([ key, field_dto ]) => {
				if( field_dto.kind === 'link' || field_dto.kind === 'links' || field_dto.kind === 'owned' ) return
				const item = host.key( key, 'auto' )
				if( !item ) return

				switch( field_dto.kind ) {
					case 'atom':
						item.val?.( this.normalize_field_value( key, field_dto.value ) )
						return
					case 'text':
						item.value?.( this.normalize_field_value( key, field_dto.value ) )
						return
					case 'list':
						const value = this.normalize_field_value( key, field_dto.value )
						if( typeof item.items === 'function' ) item.items( value )
						else if( typeof item.items_vary === 'function' ) item.items_vary( value )
						else item.val?.( value )
						return
				}
			} )
		}

		protected deserialize_entries_links( host: any, dto: $shm_hitalama_transfer_node ) {
			if( !dto.entries?.length ) return
			if( typeof host.key !== 'function' ) return

			dto.entries.forEach( ([ key, field_dto ]) => {
				if( field_dto.kind === 'owned' ) return
				const item = host.key( key, 'auto' )
				if( !item ) return

				switch( field_dto.kind ) {
					case 'link': {
						if( !field_dto.link ) return
						const pawn = this.pawn_by_old_link.get( field_dto.link )
						if( pawn ) {
							item.remote?.( pawn )
						}
						return
					}
					case 'links': {
						const links = field_dto.links.flatMap( link => {
							const pawn = this.pawn_by_old_link.get( link )
							if( pawn ) return [ pawn ]
							return []
						} )
						item.remote_list?.( links )
						return
					}
				}
			} )
		}

		blob_from_uri( uri?: string ) {
			if( !uri ) return null
			const cached = this.blobs_by_uri.get( uri )
			if( cached ) return cached

			let blob: Blob | null = null
			if( uri.startsWith( 'data:' ) ) {
				const comma = uri.indexOf( ',' )
				if( comma === -1 ) return null

				const meta = uri.slice( 5, comma )
				const data = uri.slice( comma + 1 )
				const meta_parts = meta.split( ';' )
				const base64 = meta_parts.some( part => part.toLowerCase() === 'base64' )
				const mime = meta_parts.filter( part => part.toLowerCase() !== 'base64' ).join( ';' ) || undefined

				if( base64 ) {
					blob = new Blob( [ $mol_base64_decode( data.replace( /\s/g, '' ) ) ], { type: mime } )
				} else {
					blob = new Blob( [ decodeURIComponent( data ) ], { type: mime } )
				}
			} else {
				blob = this.$.$mol_wire_sync( this.$.$mol_fetch ).blob( uri )
			}

			this.blobs_by_uri.set( uri, blob )
			return blob
		}

		blob_prime( uri: string, blob: Blob ) {
			this.blobs_by_uri.set( uri, blob )
		}

		lands() {
			return [ ... new Map(
				[ ... this.pawn_by_old_link.values() ].flatMap( pawn => {
					const land = pawn?.land?.()
					return land ? [[ land.link().str, land ] as const] : []
				} ),
			).values() ]
		}

		protected apply_blob( host: any, dto: $shm_hitalama_transfer_node ) {
			if( !dto.blob_uri ) return
			const blob = this.blob_from_uri( dto.blob_uri )
			if( !blob ) return

			if( host instanceof $giper_baza_file ) {
				host.blob( blob )
			}
		}

		protected deserialize_node_data( host: any, dto: $shm_hitalama_transfer_node ) {
			this.set_title( host, dto )
			const value = dto.fields.Value
			if( host instanceof $giper_baza_text && value?.kind === 'text' ) {
				host.value( this.normalize_field_value( 'Value', value.value ) )
			}
			for( const field in dto.fields ) this.deserialize_field_data( host, field, dto.fields[ field ] )
			this.deserialize_entries_data( host, dto )
			this.apply_blob( host, dto )
		}

		protected deserialize_node_links( host: any, dto: $shm_hitalama_transfer_node ) {
			if( dto.items?.length && typeof host.remote_list === 'function' ) {
				host.remote_list()
				host.remote_list(
					dto.items.flatMap( item_dto => {
						if( item_dto.kind !== 'link' || !item_dto.link ) return []

						const pawn = this.pawn_by_old_link.get( item_dto.link )
						if( pawn ) return [ pawn ]
						return []
					} )
				)
			}

			for( const field in dto.fields ) this.deserialize_field_links( host, field, dto.fields[ field ] )
			this.deserialize_entries_links( host, dto )
		}

		@ $mol_action
		deserialize( dto: $shm_hitalama_transfer_dump ) {
			if( !Array.isArray( dto.nodes ) ) {
				throw new Error( 'Invalid hitalama_transfer dump: nodes should be an array' )
			}

			this.pawn_by_old_link.clear()
			this.node_by_old_link = new Map( dto.nodes.map( node => [ node.link, node ] ) )
			const root = this.node_by_old_link.get( dto.root_link )
			if( !root ) throw new Error(`Transfer root node not found: ${dto.root_link}`)

			this.set_mapping( dto.root_link, this.entity() )
			this.instantiate_children( this.entity(), root )

			this.deserialize_node_data( this.entity(), root )
			dto.nodes.forEach( node => {
				const pawn = this.pawn_by_old_link.get( node.link )
				if( !pawn || node.link === dto.root_link ) return
				this.deserialize_node_data( pawn, node )
			} )

			this.deserialize_node_links( this.entity(), root )
			dto.nodes.forEach( node => {
				const pawn = this.pawn_by_old_link.get( node.link )
				if( !pawn || node.link === dto.root_link ) return
				this.deserialize_node_links( pawn, node )
			} )
		}

	}

	export class $shm_hitalama_transfer_restorer extends $shm_hitalama_transfer {
			protected class_by_name( class_name?: string ) {
				if( !class_name ) return null
				return ( this.$ as any )[ class_name ] ?? null
			}

			protected pawn_existing(
				link: string,
				class_name?: string,
				fallback?: any,
			) {
				const Pawn = fallback ?? this.class_by_name( class_name ) ?? $giper_baza_dict
				return $giper_baza_glob.Pawn( new $giper_baza_link( link ), Pawn )
			}

			protected instantiate_field_child(
				host: any,
				field: string,
				kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
				link: string | null,
			) {
				if( !link ) return
				if( this.pawn_by_old_link.has( link ) ) return
				if( !this.should_instantiate_child( host, field, kind, link ) ) return

				const child_dto = this.node_by_old_link.get( link )
				if( !child_dto ) return

				let child: any = null

				if( kind === 'owned' ) {
					child = host[ field ]?.call( host )
					if( child?.link?.().toString() !== link ) {
						child = this.pawn_existing( link, child_dto.class_name, child?.constructor )
					}
				} else {
					const accessor = host[ field ]?.call( host, null )
					if( !accessor ) return

					if( kind === 'link' ) {
						child = accessor.remote?.()
						if( child?.link?.().toString() !== link ) {
							child = this.pawn_existing( link, child_dto.class_name, (accessor as any).Value?.() )
						}
					} else {
						child = accessor.remote_list?.().find( (item: any) => item.link().toString() === link )
						if( !child ) {
							child = this.pawn_existing(
								link,
								child_dto.class_name,
								(accessor.constructor as typeof $giper_baza_list_link & { Value?: ()=> typeof $giper_baza_pawn }).Value?.(),
							)
						}
					}
				}

				if( !child ) return
				this.set_mapping( link, child )
				this.instantiate_children( child, child_dto )
			}

			protected instantiate_entry_child(
				host: any,
				key: string,
				kind: Extract< $shm_hitalama_transfer_field_kind, 'link' | 'links' | 'owned' >,
				link: string | null,
			) {
				if( !link ) return
				if( this.pawn_by_old_link.has( link ) ) return
				if( typeof host.key !== 'function' ) return
				if( !this.should_instantiate_child( host, key, kind, link ) ) return

				const child_dto = this.node_by_old_link.get( link )
				if( !child_dto ) return

				const item = host.key( key )
				if( !item ) return

				let child: any = null
				if( kind === 'owned' ) {
					child = item
					if( child?.link?.().toString() !== link ) {
						child = this.pawn_existing( link, child_dto.class_name, child?.constructor )
					}
				} else if( kind === 'link' ) {
					child = item.remote?.()
					if( child?.link?.().toString() !== link ) {
						child = this.pawn_existing( link, child_dto.class_name, (item as any).Value?.() )
					}
				} else {
					child = item.remote_list?.().find( (remote: any) => remote.link().toString() === link )
					if( !child ) {
						child = this.pawn_existing(
							link,
							child_dto.class_name,
							(item.constructor as typeof $giper_baza_list_link & { Value?: ()=> typeof $giper_baza_pawn }).Value?.(),
						)
					}
				}

				if( !child ) return
				this.set_mapping( link, child )
				this.instantiate_children( child, child_dto )
			}

			protected instantiate_children(
				host: any,
				dto: $shm_hitalama_transfer_node,
			) {
				if( dto.items?.length && typeof host.remote_list === 'function' ) {
					dto.items.forEach( item_dto => {
						if( item_dto.kind !== 'link' || !item_dto.link ) return
						if( this.pawn_by_old_link.has( item_dto.link ) ) return
						if( !this.should_instantiate_child( host, 'items', 'links', item_dto.link ) ) return

						const child_dto = this.node_by_old_link.get( item_dto.link )
						if( !child_dto ) return

						let child = host.remote_list?.().find( (item: any) => item.link().toString() === item_dto.link )
						if( !child ) {
							child = this.pawn_existing(
								item_dto.link,
								child_dto.class_name,
								(host.constructor as typeof $giper_baza_list_link & { Value?: ()=> typeof $giper_baza_pawn }).Value?.(),
							)
						}

						if( !child ) return
						this.set_mapping( item_dto.link, child )
						this.instantiate_children( child, child_dto )
					} )
				}

				for( const field in dto.fields ) {
					const field_dto = dto.fields[ field ]
					if( field_dto.kind === 'owned' ) {
						this.instantiate_field_child( host, field, 'owned', field_dto.link )
						continue
					}
					if( field_dto.kind === 'link' ) {
						this.instantiate_field_child( host, field, 'link', field_dto.link )
						continue
					}
					if( field_dto.kind === 'links' ) {
						field_dto.links.forEach( link => this.instantiate_field_child( host, field, 'links', link ) )
					}
				}

				dto.entries?.forEach( ([ key, field_dto ]) => {
					if( field_dto.kind === 'owned' ) {
						this.instantiate_entry_child( host, key, 'owned', field_dto.link )
						return
					}
					if( field_dto.kind === 'link' ) {
						this.instantiate_entry_child( host, key, 'link', field_dto.link )
						return
					}
					if( field_dto.kind === 'links' ) {
						field_dto.links.forEach( link => this.instantiate_entry_child( host, key, 'links', link ) )
					}
				} )
			}

			protected set_mapping( old_link: string, pawn: any ) {
				this.pawn_by_old_link.set( old_link, pawn )
			}
		}

}
